import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

import type { EngineDocumentResponseExpanded } from '@engine/models/document'

import { BulkUpdateToolbar } from '#components/BulkUpdateToolbar'
import { Table } from '#components/Table'
import { useBulkUpdate } from '#hooks/useBulkUpdate'

import { DocumentStatusBadge } from './DocumentStatusBadge.js'

interface DocumentsListProps {
  documents: EngineDocumentResponseExpanded[]
  selectedId?: string
  onSelect: (document: EngineDocumentResponseExpanded) => void
  infiniteScrollRef?: React.RefObject<HTMLDivElement>
  isFetchingMore?: boolean
  sortBy?: string
  onSortChange?: (sortBy: string) => void
  onUpdatesTriggered?: () => void
}

const columnHelper = createColumnHelper<EngineDocumentResponseExpanded>()

const getUpdateEndpoint = (id: string) => `documents/${id}/update`

const getQueuedMessage = (count: number) =>
  `Queued ${count} document${count === 1 ? '' : 's'} for processing.`

const DocumentsList = ({
  documents,
  selectedId,
  onSelect,
  infiniteScrollRef,
  isFetchingMore,
  sortBy,
  onSortChange,
  onUpdatesTriggered,
}: DocumentsListProps) => {
  const visibleDocumentIds = useMemo(
    () => documents.map(document => document._id),
    [documents],
  )

  const bulkUpdate = useBulkUpdate({
    visibleIds: visibleDocumentIds,
    getEndpoint: getUpdateEndpoint,
    successMessage: getQueuedMessage,
    noun: 'document',
    onUpdatesTriggered,
  })
  const { selectedIds, toggleSelection } = bulkUpdate

  const handleSort = (columnId: string) => {
    if (!onSortChange) return

    const isDefaultColumn = columnId === 'createdAt'

    if (sortBy === columnId) {
      onSortChange(`-${columnId}`)
    } else if (sortBy === `-${columnId}`) {
      if (isDefaultColumn) {
        onSortChange(columnId)
      } else {
        onSortChange('-createdAt')
      }
    } else {
      onSortChange(columnId)
    }
  }

  const getSortState = (columnId: string): 'asc' | 'desc' | false => {
    if (sortBy === columnId) return 'asc'
    if (sortBy === `-${columnId}`) return 'desc'
    return false
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('externalId', {
        header: 'External ID',
        cell: info => (
          <div className="truncate max-w-[200px]">{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('trackingNumber', {
        header: 'Tracking#',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('carrierCode', {
        header: 'SCAC',
        cell: info => (
          <span className="badge badge-sm badge-info uppercase">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: info => <DocumentStatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor('pickupDate', {
        header: 'Pickup Date',
        enableSorting: true,
        cell: info => {
          const date = info.getValue()
          return date ? <span className="text-sm">{date}</span> : null
        },
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created At',
        enableSorting: true,
        cell: info => (
          <span className="text-xs">
            {new Date(info.getValue()).toLocaleString()}
          </span>
        ),
      }),
      columnHelper.accessor('lastUpdated', {
        header: 'Last Updated',
        enableSorting: true,
        cell: info => {
          const date = info.getValue()
          return date ? (
            <span className="text-xs">{new Date(date).toLocaleString()}</span>
          ) : null
        },
      }),
      columnHelper.accessor('nextRunDate', {
        header: 'Next Run',
        enableSorting: true,
        cell: info => {
          const date = info.getValue()
          return date ? (
            <span className="text-xs">{new Date(date).toLocaleString()}</span>
          ) : (
            <span className="text-xs opacity-60">Not queued</span>
          )
        },
      }),
      columnHelper.display({
        id: 'selectForUpdate',
        header: 'Fetch',
        cell: info => {
          const id = info.row.original._id
          return (
            <input
              type="checkbox"
              className="checkbox checkbox-xs"
              checked={selectedIds.has(id)}
              onChange={() => toggleSelection(id)}
              onClick={event => event.stopPropagation()}
              aria-label={`Select document ${id}`}
            />
          )
        },
        enableSorting: false,
      }),
    ],
    [sortBy, onSortChange, selectedIds, toggleSelection],
  )

  return (
    <div className="flex flex-col gap-3 h-full">
      <BulkUpdateToolbar state={bulkUpdate} label="Fetch Document" />
      <Table
        data={documents}
        columns={columns}
        getRowId={document => document._id}
        selectedId={selectedId}
        onRowClick={onSelect}
        infiniteScrollRef={infiniteScrollRef}
        isFetchingMore={isFetchingMore}
        manualSorting
        onSortChange={handleSort}
        getSortState={getSortState}
      />
    </div>
  )
}

export { DocumentsList }
